import { useEffect, useState } from "react";
import { ChevronLeft, Rocket, Send, Sparkles, CheckCircle2, TrendingUp, Map, ArrowRight, Target, GitCompare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { Loader, Bullets, Chips, SectionCard } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function FitScore({ value }) {
  return (
    <div className="flex items-center gap-4">
      <div className="relative h-20 w-20 shrink-0">
        <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
          <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(var(--secondary))" strokeWidth="4" />
          <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(var(--primary))" strokeWidth="4" strokeDasharray={`${(value / 100) * 94} 94`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 grid place-items-center font-head font-extrabold text-xl">{value}</span>
      </div>
      <div><div className="font-head font-bold">Fit signal</div><div className="text-sm text-muted-foreground">How your approach matched this role</div></div>
    </div>
  );
}

export default function Career() {
  const { mode } = useAppMode();
  const [careers, setCareers] = useState([]);
  const [active, setActive] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState(null);
  const [pathLoading, setPathLoading] = useState(false);

  useEffect(() => { api.get("/careers").then((r) => setCareers(r.data.careers)).catch(() => {}); }, []);

  const openCareer = async (c) => {
    setActive(c); setChallenge(null); setResult(null); setAnswer(""); setPath(null); setLoading(true);
    try {
      const { data } = await api.post("/career/challenge", { career: c.title, mode });
      setChallenge(data.error ? null : data);
    } catch { toast.error("Could not load the challenge."); }
    finally { setLoading(false); }
  };

  const buildPath = async () => {
    setPathLoading(true); setPath(null);
    const ctx = result
      ? `They attempted a task: "${challenge?.task || ""}". Their answer: "${answer}". Evaluation — strengths: ${(result.strengths || []).join("; ")}. Gaps to develop: ${(result.gaps || []).join("; ")}. Fit verdict: ${result.verdict || ""}.`
      : null;
    try {
      const { data } = await api.post("/career/path", { career: active.title, mode, context: ctx });
      setPath(data.error ? null : data);
    } catch { toast.error("Could not build the path."); }
    finally { setPathLoading(false); }
  };

  const savePath = async () => {
    await api.post("/ideas", { title: `Career Path: ${active.title}`, data: { tool: "career-path", career: active.title, path } });
    toast.success("Saved to your Idea Vault");
  };

  const addPathGoals = async (p) => {
    const titles = [];
    if (p.first_action) titles.push(p.first_action);
    (p.phases || []).forEach((ph) => { if (ph.milestone) titles.push(ph.milestone); else if (ph.title) titles.push(ph.title); });
    const { data } = await api.post("/goals/bulk", { titles, domain: "career" });
    toast.success(`Added ${data.added} goals to your Home dashboard`);
  };

  // compare mode
  const [view, setView] = useState("drive");
  const [ca, setCa] = useState("");
  const [cb, setCb] = useState("");
  const [cmp, setCmp] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);

  const runCompare = async () => {
    if (!ca || !cb || ca === cb) { toast.info("Pick two different careers."); return; }
    setCmpLoading(true); setCmp(null);
    try {
      const { data } = await api.post("/career/compare", { career_a: ca, career_b: cb, mode });
      setCmp(data.error ? null : data);
    } catch { toast.error("Could not compare."); }
    finally { setCmpLoading(false); }
  };

  const submit = async () => {
    if (!answer.trim()) { toast.info("Give the task a try first — there's no wrong answer."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/career/evaluate", { career: active.title, task: challenge.task, response: answer, mode });
      setResult(data.error ? null : data);
    } catch { toast.error("Could not evaluate."); }
    finally { setLoading(false); }
  };

  if (active) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <button onClick={() => setActive(null)} data-testid="career-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4" />All careers</button>
        <div className="flex items-center gap-3">
          <div className="text-4xl">{active.emoji}</div>
          <div><h1 className="font-head font-extrabold text-3xl">Test-Drive: {active.title}</h1><p className="text-muted-foreground">{active.blurb}</p></div>
        </div>

        {loading && !challenge && <Loader label="Setting up your on-the-job challenge…" />}

        {challenge && (
          <div className="space-y-5">
            {challenge.day_in_life && <SectionCard title="A moment on the job"><p className="leading-relaxed text-base">{challenge.day_in_life}</p></SectionCard>}
            {challenge.skills_used?.length > 0 && <SectionCard title="Skills this job leans on"><Chips items={challenge.skills_used} /></SectionCard>}
            <SectionCard title="Your challenge" icon={Rocket} accent>
              <p className="leading-relaxed text-base">{challenge.task}</p>
              {challenge.what_good_looks_like?.length > 0 && (
                <div className="mt-4"><p className="text-sm font-semibold mb-2 text-muted-foreground">A strong answer usually:</p><Bullets items={challenge.what_good_looks_like} /></div>
              )}
            </SectionCard>

            {!result && (
              <div className="rounded-lg border border-border bg-card p-5">
                <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={6} data-testid="career-answer"
                  placeholder="Type how you'd handle this…" className="w-full rounded-md border border-input bg-background px-3 py-3 outline-none focus:ring-2 focus:ring-ring resize-none" />
                <button onClick={submit} disabled={loading} data-testid="career-submit" className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 transition">
                  <Send className="w-4 h-4" /> Submit my attempt
                </button>
              </div>
            )}

            {loading && result === null && answer && <Loader label="Reviewing your work like a mentor would…" />}

            {result && (
              <div className="space-y-4 animate-rise">
                {typeof result.fit_score === "number" && <SectionCard title="Your test-drive result" accent><FitScore value={result.fit_score} /></SectionCard>}
                {result.verdict && <SectionCard title="Honest verdict"><p className="text-lg">{result.verdict}</p></SectionCard>}
                {result.strengths?.length > 0 && <SectionCard title="What you did well" icon={CheckCircle2}><Bullets items={result.strengths} /></SectionCard>}
                {result.gaps?.length > 0 && <SectionCard title="What to develop"><Bullets items={result.gaps} /></SectionCard>}
                {result.enjoyment_signals && <SectionCard title="Would you enjoy this?" icon={TrendingUp}><p>{result.enjoyment_signals}</p></SectionCard>}
                {result.next_steps?.length > 0 && <SectionCard title="Explore further"><Bullets items={result.next_steps} /></SectionCard>}
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => openCareer(active)} data-testid="career-retry" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary"><Sparkles className="w-4 h-4" />Try a new challenge</button>
                  <button onClick={buildPath} disabled={pathLoading} data-testid="career-build-path" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 transition"><Map className="w-4 h-4" />Build my career path</button>
                </div>

                {pathLoading && <Loader label="Mapping your route into this career…" />}
                {path && (
                  <div className="space-y-4 animate-rise" data-testid="career-path">
                    <h2 className="font-head font-extrabold text-2xl flex items-center gap-2"><Map className="w-6 h-6 text-primary" />Your path into {active.title}</h2>
                    {path.summary && <p className="leading-relaxed">{path.summary}</p>}
                    {path.skills?.length > 0 && <SectionCard title="Skills to build"><Chips items={path.skills} /></SectionCard>}
                    {path.phases?.map((ph, i) => (
                      <SectionCard key={i} title={`Phase ${i + 1}: ${ph.title}${ph.duration ? " · " + ph.duration : ""}`}>
                        <Bullets items={ph.steps} />
                        {ph.milestone && <p className="mt-3 text-sm"><b className="text-primary">Milestone:</b> {ph.milestone}</p>}
                      </SectionCard>
                    ))}
                    {path.habits?.length > 0 && <SectionCard title="Ongoing habits"><Bullets items={path.habits} /></SectionCard>}
                    {path.first_action && <SectionCard title="Start this week" icon={ArrowRight} accent><p>{path.first_action}</p></SectionCard>}
                    {path.risks?.length > 0 && <SectionCard title="Realistic obstacles"><Bullets items={path.risks} /></SectionCard>}
                    <div className="flex flex-wrap gap-3">
                      <button onClick={() => addPathGoals(path)} data-testid="career-path-to-goals" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 transition"><Target className="w-4 h-4" />Add to my goals</button>
                      <button onClick={savePath} data-testid="career-path-save" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary"><Sparkles className="w-4 h-4" />Save this path</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl flex items-center gap-3"><Rocket className="w-8 h-8 text-primary" />Career Test-Drive</h1>
        <p className="text-muted-foreground mt-2">Don't just read about a job — actually try it. Take a hands-on mini-challenge and get honest feedback on the fit.</p>
      </div>

      <div className="flex rounded-full bg-secondary p-1 w-full max-w-sm">
        {[{ k: "drive", l: "Test-Drive", i: Rocket }, { k: "compare", l: "Compare two", i: GitCompare }].map((t) => (
          <button key={t.k} onClick={() => setView(t.k)} data-testid={`career-view-${t.k}`}
            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2 text-sm font-bold transition-colors ${view === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <t.i className="w-4 h-4" />{t.l}
          </button>
        ))}
      </div>

      {view === "compare" ? (
        <div className="space-y-5">
          <div className="rounded-lg border border-border bg-card p-5 grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-center">
            <Select value={ca} onValueChange={setCa}>
              <SelectTrigger data-testid="compare-a"><SelectValue placeholder="Choose career A" /></SelectTrigger>
              <SelectContent>{careers.map((c) => <SelectItem key={c.slug} value={c.title}>{c.emoji} {c.title}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-center font-head font-bold text-muted-foreground">vs</span>
            <Select value={cb} onValueChange={setCb}>
              <SelectTrigger data-testid="compare-b"><SelectValue placeholder="Choose career B" /></SelectTrigger>
              <SelectContent>{careers.map((c) => <SelectItem key={c.slug} value={c.title}>{c.emoji} {c.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <button onClick={runCompare} disabled={cmpLoading} data-testid="compare-run" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 transition"><GitCompare className="w-4 h-4" />Compare</button>

          {cmpLoading && <Loader label="Weighing them up honestly…" />}
          {cmp && (
            <div className="space-y-4 animate-rise" data-testid="compare-result">
              {cmp.key_difference && <SectionCard title="The key difference" accent><p className="text-lg">{cmp.key_difference}</p></SectionCard>}
              <div className="grid md:grid-cols-2 gap-4">
                {[cmp.a, cmp.b].map((col, i) => col && (
                  <div key={i} className="rounded-lg border border-border bg-card p-5 space-y-3">
                    <h3 className="font-head font-extrabold text-xl">{col.name}</h3>
                    {col.snapshot && <p className="text-sm text-muted-foreground">{col.snapshot}</p>}
                    {col.day_to_day && <p className="text-sm"><b>Day to day:</b> {col.day_to_day}</p>}
                    {col.pros?.length > 0 && <div><p className="text-sm font-semibold text-green-500 mb-1">Pros</p><Bullets items={col.pros} /></div>}
                    {col.cons?.length > 0 && <div><p className="text-sm font-semibold text-destructive mb-1">Cons</p><Bullets items={col.cons} /></div>}
                    {col.core_skills?.length > 0 && <div><p className="text-sm font-semibold mb-1">Core skills</p><Chips items={col.core_skills} /></div>}
                    {col.typical_path && <p className="text-sm"><b>Typical path:</b> {col.typical_path}</p>}
                    {col.earning_note && <p className="text-sm text-muted-foreground">{col.earning_note}</p>}
                  </div>
                ))}
              </div>
              {(cmp.who_suits_a || cmp.who_suits_b) && (
                <SectionCard title="Who each suits">
                  {cmp.who_suits_a && <p className="text-sm"><b>{cmp.a?.name}:</b> {cmp.who_suits_a}</p>}
                  {cmp.who_suits_b && <p className="text-sm mt-1"><b>{cmp.b?.name}:</b> {cmp.who_suits_b}</p>}
                </SectionCard>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {careers.map((c) => (
            <button key={c.slug} onClick={() => openCareer(c)} data-testid={`career-${c.slug}`}
              className="text-left rounded-lg border border-border bg-card p-5 hover:border-primary hover:-translate-y-0.5 transition-all">
              <div className="text-3xl">{c.emoji}</div>
              <div className="font-head font-bold text-lg mt-2">{c.title}</div>
              <div className="text-sm text-muted-foreground">{c.blurb}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
